import { FC, PropsWithChildren, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Content from '../../../components/Content/Content';
import DonationHeader from '../../../components/DonationHeader/DonationHeader';
import Loader from '../../../components/Loader/Loader';
import { getButtonData, getCurrenciesData } from '../../../redux/DonationSlice/DonationSelectors';
import { DonationStage } from '../../../redux/DonationSlice/DonationSlice';
import FooterActions from '../../../redux/FooterSlice/FooterActions';
import { useAppDispatch } from '../../../redux/Store';
import TestIds from '../../../tests/TestIds';
import DonationContext from '../Context/DonationContext';
import CurrencySelect from './CurrencySelect/CurrencySelect';
import Donate from './Donate/Donate';

import styles from './DonationView.module.scss';

const messages = defineMessages({
	metaDescription: {
		id: 'payments.donation_meta',
		defaultMessage: 'Donation information',
	},
	donateWithSc: {
		id: 'payments.donate_with_sc',
		defaultMessage: 'Donate through SpectroCoin',
	},
});

const ContentWrapper: FC<PropsWithChildren> = ({ children }) => {
	const { description, id } = useSelector(getButtonData)!;
	const { locale, formatMessage } = useIntl();
	const {
		stage,
		address: { data: address },
	} = useContext(DonationContext);
	const currencies = useSelector(getCurrenciesData)!;

	return (
		<>
			<Helmet
				htmlAttributes={{ lang: locale }}
				title={`SpectroCoin - ${formatMessage(messages.metaDescription)}`}
				meta={[{ name: 'description', content: formatMessage(messages.metaDescription) }]}
			/>
			<Content>
				<div className={styles.container} data-cy={TestIds.Donation}>
					<DonationHeader
						description={description}
						id={id}
						currencyInfo={
							stage === DonationStage.Donate && address
								? {
										currencyCode: address.payCurrencyCode,
										currencyName: currencies.find(
											({ code }) => code === address.payCurrencyCode
										)!.name,
								  }
								: undefined
						}
					/>
					{children}
				</div>
			</Content>
		</>
	);
};

const DonationView: FC = () => {
	const { locale, formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const { id } = useSelector(getButtonData)!;
	const {
		stage,
		setStage,
		address: { data: address },
		createAddress,
	} = useContext(DonationContext);
	const currencies = useSelector(getCurrenciesData)!;

	const content = (() => {
		switch (stage) {
			case DonationStage.CurrencySelect:
				return (
					<ContentWrapper>
						<CurrencySelect />
					</ContentWrapper>
				);
			case DonationStage.Donate:
				return (
					<ContentWrapper>
						<Donate />
					</ContentWrapper>
				);
			default:
				return <Loader />;
		}
	})();

	useEffect(() => {
		dispatch(
			FooterActions.setLink({
				label: formatMessage(messages.donateWithSc),
				src:
					stage === DonationStage.Donate && address
						? `/${locale}/payments/donations/${id}/${address.payCurrencyCode}`
						: `/${locale}/payments/donations/${id}`,
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, address]);

	useEffect(() => {
		if (currencies.length === 1) {
			createAddress(currencies[0].code)
				.then(() => setStage(DonationStage.Donate))
				.catch(() => setStage(DonationStage.CurrencySelect));
		} else {
			setStage(DonationStage.CurrencySelect);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currencies.length]);

	return content;
};

export default DonationView;
